import { useEffect } from 'react'
import { useSetState } from '@domain-group/react-hooks'

import type { TaskListItemModel } from '@types/models'

import useSelector from '@hooks/useSelector'
import useTaskList from '@hooks/useTaskList'
import useTaskListItems from '@hooks/useTaskListItems'

type CalculateItemsToTotalPointsParams = {
  taskListItems: TaskListItemModel[],
}

const calculateItemsTotalPoints = (params: CalculateItemsToTotalPointsParams) => {
  const { taskListItems } = params

  const total = taskListItems.reduce((acc: number, taskListItem: TaskListItemModel) => {
    const assignedPoints = taskListItem.assigned_points ?? 0
    return acc + assignedPoints
  }, 0)

  return total
}

type CalculateChecklistScoreParams = {
  failedTaskListItems: TaskListItemModel[],
  filteredTaskListItems: TaskListItemModel[],
}

const calculateChecklistScore = (params: CalculateChecklistScoreParams) => {
  const { failedTaskListItems, filteredTaskListItems } = params

  const availablePoints = calculateItemsTotalPoints({ taskListItems: filteredTaskListItems })
  const failedPoints = calculateItemsTotalPoints({ taskListItems: failedTaskListItems })

  const score = (availablePoints - failedPoints) / availablePoints

  return {
    availablePoints,
    failedPoints,
    receivedPoints: availablePoints - failedPoints,
    score,
  }
}

type DefaultState = {
  activeStepKey: 'overview' | 'summary',
}

const defaultState: DefaultState = {
  activeStepKey: 'overview',
}

type UseProcessChecklistOptions = {
  taskListId: number,
}

function useProcessChecklist(options: UseProcessChecklistOptions) {
  const { taskListId } = options

  const [state, setState] = useSetState(defaultState)
  const { activeStepKey } = state

  const taskListPayload = useTaskList({ id: taskListId })
  const { callbacks: { toggleCompletion }, taskList } = taskListPayload

  useEffect(() => {
    if (taskList.completed){
      setState({ activeStepKey: 'summary' })
    }
  }, [taskList.completed])

  const { loading: loadingTaskListItems } = useSelector(reduxState => reduxState.taskListItems)

  const taskListItemsPayload = useTaskListItems({
    filters: {
      task_list_id: taskList.id,
    },
    performHttpRequests: !!taskList.id,
  })

  const { filteredTaskListItems } = taskListItemsPayload

  const failedTaskListItemsPayload = useTaskListItems({
    filters: {
      completed: false,
      task_list_id: taskList.id,
    },
    performHttpRequests: false,
  })

  const { filteredTaskListItems: failedTaskListItems } = failedTaskListItemsPayload

  const calculatedScore = calculateChecklistScore({
    failedTaskListItems,
    filteredTaskListItems,
  })

  return {
    activeStepKey,
    calculatedScore,
    callbacks: {
      goToStep: (stepKey: 'overview' | 'summary') => setState({ activeStepKey: stepKey }),
      submitChecklist: () => toggleCompletion({
        id: taskListId,
        checklist_score: {
          available_points: calculatedScore.availablePoints,
          failed_points: calculatedScore.failedPoints,
          received_points: calculatedScore.receivedPoints,
        },
      }),
    },
    failedTaskListItemsPayload,
    loadingTaskListItems,
    taskList,
    taskListItemsPayload,
    taskListPayload,
  }
}

export default useProcessChecklist
